import { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { ScheduleSummary } from "../../services/scheduling/Scheduling.types";
import { getScheduleColor } from "./calendar.utils";
import MiniCalendar from "./MiniCalendar";
import styles from "./ScheduleCalendar.module.css";

interface ScheduleSidebarProps {
  schedules: ScheduleSummary[];
  selectedScheduleIds: string[];
  selectedDate: Dayjs;
  onSelectedScheduleIdsChange: (ids: string[]) => void;
  onSelectDate: (date: Dayjs) => void;
}

export default function ScheduleSidebar({
  schedules,
  selectedScheduleIds,
  selectedDate,
  onSelectedScheduleIdsChange,
  onSelectDate,
}: ScheduleSidebarProps) {
  const { t } = useTranslation();

  const toggleSchedule = (scheduleId: string) => {
    if (selectedScheduleIds.includes(scheduleId)) {
      onSelectedScheduleIdsChange(
        selectedScheduleIds.filter((id) => id !== scheduleId),
      );
    } else {
      onSelectedScheduleIdsChange([...selectedScheduleIds, scheduleId]);
    }
  };

  return (
    <aside className={styles.sidebar}>
      <MiniCalendar selectedDate={selectedDate} onSelectDate={onSelectDate} />
      <section className={styles.scheduleSelector}>
        <div className={styles.scheduleSelectorHeader}>
          <h3>{t("calendar.sidebar.title")}</h3>
          {schedules.length > 0 && (
            <button
              type="button"
              onClick={() =>
                onSelectedScheduleIdsChange(
                  selectedScheduleIds.length === schedules.length
                    ? []
                    : schedules.map((schedule) => schedule.id),
                )
              }
            >
              {selectedScheduleIds.length === schedules.length
                ? t("calendar.sidebar.clearAll")
                : t("calendar.sidebar.selectAll")}
            </button>
          )}
        </div>

        <div className={styles.scheduleList}>
          {schedules.map((schedule) => (
            <label key={schedule.id} className={styles.scheduleOption}>
              <input
                type="checkbox"
                checked={selectedScheduleIds.includes(schedule.id)}
                onChange={() => toggleSchedule(schedule.id)}
              />
              <span
                className={styles.scheduleDot}
                style={{ backgroundColor: getScheduleColor(schedule.id, schedules) }}
              />
              <span className={styles.scheduleOptionText}>
                <strong>
                  {schedule.kind === "unlinked"
                    ? t("calendar.unlinked")
                    : schedule.title}
                </strong>
                <small>
                  {t(`calendar.actorTypes.${schedule.actorType}`)}
                  {schedule.kind === "unlinked"
                    ? ` · ${schedule.subtitle || schedule.actorReference}`
                    : schedule.subtitle
                      ? ` · ${schedule.subtitle}`
                      : ""}
                </small>
              </span>
            </label>
          ))}
        </div>
      </section>
    </aside>
  );
}
